import { createContext, useContext } from 'react';
import type {
  TodoItemResponse,
  TodoSectionResponse,
  TodoItemStatus,
  TodoProjectResponse,
} from '@/lib/api';
import type { BulkActionsProps } from '../components/BulkActionsMenuItems';

export interface TodoListContextValue {
  // Data
  /**
   * Whether the current user can MANAGE this list — create / delete / reorder /
   * assign, and edit any item or section. True for the owner of a Personal list and
   * for owner/admin members of a shared (Org) workspace. A plain member is NOT a
   * manager; they may only edit items / sections assigned to them (compared against
   * currentPrincipalId). The server still enforces the precise Vault role on write.
   */
  isOwner: boolean;
  currentUserId: string | undefined;
  /** The current user's Vault Principal ID, for "assigned to me" edit checks. */
  currentPrincipalId: string | undefined;
  /** Whether this list is public (has public sharing enabled). Gates the
   * per-item / per-section public-visibility eye toggles — they're meaningless
   * on a private list, so the chrome is hidden entirely when this is false. */
  isListPublic?: boolean;
  visibleSectionIds: Set<string> | undefined;
  /** The list's item-id prefix (e.g. "FR"); combined with an item's `seq` to
   * render its display id "FR-12". Absent in read-only/public contexts. */
  itemKeyPrefix?: string;
  /** Width, in `ch`, of the widest display id this list can currently show
   * (prefix + dash + the digits of its seq counter). The item row reserves
   * this width for every id so titles line up regardless of "FR-7" vs "FR-128". */
  itemIdWidthCh?: number;
  /** Whether this list requires items to pass an "In Review" step before they
   * can be completed. Drives the checkbox semantics and the Verify/Send-back
   * controls in {@link TodoItemRow}. Absent/false = classic behavior. */
  reviewMode?: boolean;

  // Callbacks (optional — not provided for public/read-only views)
  onToggleItem?: (item: TodoItemResponse) => void;
  /** Approve a review item (in_review → completed). Only meaningful in review mode. */
  onApproveItem?: (item: TodoItemResponse) => void;
  /** Send a review item back to active work (in_review → in_progress). */
  onRejectItem?: (item: TodoItemResponse) => void;
  onEditItem?: (item: TodoItemResponse) => void;
  onDeleteItem?: (itemId: string) => void;
  /** Manually set an item's workflow status (Todo / In Progress / Blocked /
   * In Review / Cancelled). Available to managers and the item's assignee. */
  onSetItemStatus?: (itemId: string, status: TodoItemStatus) => void;
  onEditSection?: (section: TodoSectionResponse) => void;
  onDeleteSection?: (sectionId: string) => void;
  /** Open the "Move to list…" dialog for a top-level task (manager-only; moves the
   * task + its sub-tasks into another list in the workspace). */
  onMoveItem?: (item: TodoItemResponse) => void;
  /** Open the "Move to list…" dialog for a section (manager-only; moves the whole
   * subtree + its tasks into another list in the workspace). */
  onMoveSection?: (section: TodoSectionResponse) => void;
  onCreateItem?: (input: { title: string; description?: string; sectionId?: string }) => void;
  onCreateSection?: (input: {
    name: string;
    description?: string;
    parentSectionId?: string;
    sectionType?: 'timeframe' | 'category';
  }) => void;
  onReorderItems?: (sectionId: string | null, itemIds: string[]) => void;
  onReorderSections?: (sectionIds: string[]) => void;
  onUpdateItemTitle?: (itemId: string, title: string) => void;
  onCreateItemBelow?: (
    sectionId: string | null,
    afterItemId: string,
    title: string,
  ) => Promise<void>;
  /**
   * Sub-tasks of the shared list, grouped by their parent item id — rendered
   * nested (indented, collapsible, inert) beneath their parent in the List view.
   * Sub-tasks are NOT draggable on the shared list (locked to their parent); only
   * the My Tasks board makes them movable.
   */
  subtasksByParent?: Map<string, TodoItemResponse[]>;
  /**
   * Create a sub-task under `parentItemId` (the "+S" affordance). For "+S" on an
   * existing sub-task, the caller passes that sub-task's own parent id, producing
   * a sibling. One level deep — the server rejects a sub-task of a sub-task.
   */
  onCreateSubtask?: (parentItemId: string, title: string) => Promise<void>;
  /** Toggle a SECTION's public visibility (owner-only; gated on {@link isListPublic}).
   * The section gate is inherited — hiding it hides its tasks and sub-sections. */
  onToggleHiddenFromPublic?: (sectionId: string, hidden: boolean) => void;
  /** Toggle a single TASK's public visibility (owner-only; gated on {@link isListPublic}). */
  onToggleItemHidden?: (itemId: string, hidden: boolean) => void;

  // Item assignment (shared/Org workspaces only; manager-only)
  onAssignItem?: (itemId: string, assigneePrincipalId: string) => void;
  onUnassignItem?: (itemId: string) => void;

  // Section ACCESS (shared/Org workspaces only). Replaces single-assignee section
  // assignment: an owner/admin grants one or more members access to a section,
  // and access cascades to the section's subsections.
  /** Replace a section's access set (owner/admin only). */
  onSetSectionAccess?: (sectionId: string, principalIds: string[]) => void;
  /** Direct access grants per section id (NOT cascaded) — drives the badge and
   *  the "Give access" dialog's current selection. */
  sectionAccessBySectionId?: Map<string, string[]>;
  /** Section ids the current principal can manage tasks in — direct grants PLUS
   *  the subsection cascade (access on an ancestor covers descendants). Gates the
   *  add-item / edit-task affordances for a plain member. */
  accessibleSectionIds?: Set<string>;

  // Projects (workspace-scoped, cross-cutting task grouping).
  /** projectId → project, for rendering the project chip on a task row. */
  projectById?: Map<string, TodoProjectResponse>;
  /** Workspace projects (ordered) — the options for the row's "Set project" quick
   *  action. Empty/undefined hides the action. */
  projects?: TodoProjectResponse[];
  /** Set/clear a task's project straight from its actions menu (null clears).
   *  Available to managers and the item's assignee. */
  onSetItemProject?: (itemId: string, projectId: string | null) => void;

  // Section moves (the "Move to section" quick action) ----------------------
  /**
   * Pre-flattened, access-filtered Section targets for the row's "Move to
   * section" action — a manager gets every section; a member only the sections
   * they may move a task into. Indented by tree `depth`. Empty/undefined hides
   * the action.
   */
  movableSectionOptions?: Array<{ sectionId: string; name: string; depth: number }>;
  /** Whether the user may move a task to Unsorted (manager only — mirrors the
   *  server gate that forbids members un-sorting a task). */
  canMoveToUnsorted?: boolean;
  /** Move a task to a Section (null = Unsorted) straight from its actions menu. */
  onSetItemSection?: (itemId: string, sectionId: string | null) => void;
  /** Per-list display toggles (owner/admin-configured). Default true. */
  showProjectOnTasks?: boolean;
  showSectionPathOnTasks?: boolean;
  /** Resolve a section id to its ancestor path (root → leaf) for the section-path
   *  chip shown in the project view. */
  resolveSectionPath?: (sectionId: string | null) => string[];
  /**
   * Active members of the current workspace that an item or section can be assigned
   * to. Empty for Personal Holders.
   */
  assignableMembers?: Array<{ principalId: string; label: string; role?: string }>;
  /** Resolve a principalId to a display name, for the "assigned to" badge. */
  resolvePrincipalName?: (principalId: string | null | undefined) => string;

  // Assignment notifications (unread indicators). Item/section ids the current
  // user has an unread "assigned to you" notification for — drives the dots.
  unreadAssignedItemIds?: Set<string>;
  unreadAssignedSectionIds?: Set<string>;
  /**
   * Mark one item/section assignment read — fired when the user clicks the
   * assigned item/section, so its dot clears (strictly per-item; other lists and
   * workspaces are untouched).
   */
  onViewAssignment?: (kind: 'item' | 'section', targetId: string) => void;

  // Reviewers ---------------------------------------------------------------
  /** Item ids the current user has an unread "review requested" notification
   * for — drives the amber review dot (distinct from the red assignment dot). */
  unreadReviewItemIds?: Set<string>;
  /** Mark one review notification read — fired when the reviewer opens the item. */
  onViewReview?: (itemId: string) => void;
  /**
   * Send an item for review: sets status → in_review and routes the request to
   * one reviewer, or `null` to broadcast to all list reviewers. Provided only on
   * shared lists where the caller can edit the item.
   */
  onRequestReview?: (itemId: string, reviewerPrincipalId: string | null) => void;
  /**
   * Eligible review targets for the "Send for review" picker — the list's
   * designated reviewers plus owners/admins. Empty on Personal lists / when the
   * list has no reviewers and the caller isn't offering an escalation target.
   */
  reviewerTargets?: Array<{ principalId: string; label: string }>;
  /**
   * Whether the current user is a designated reviewer of THIS list. Grants the
   * review affordances (the Verify / Send-back controls and the actions menu) on
   * in-review items even when the item isn't assigned to them — the client
   * counterpart to the server's reviewer capability.
   */
  isListReviewer?: boolean;

  // Multi-select (bulk actions) ---------------------------------------------
  /** Item ids currently selected for a bulk action; drives the row highlight. */
  selectedItemIds?: Set<string>;
  /** True when ≥1 task is selected — a plain click on a selectable row then
   *  extends the selection instead of doing its normal thing. */
  selectionActive?: boolean;
  /**
   * Select from a click on a selectable (editable) row. The modifiers drive the
   * gesture (plain = just this, shift = range, cmd/ctrl = toggle). Provided only
   * where multi-select is enabled; the row calls it in place of its normal click.
   */
  onItemSelect?: (
    itemId: string,
    mods: { shiftKey: boolean; metaKey: boolean; ctrlKey: boolean },
  ) => void;
  /** How many tasks are selected — drives the ⋯ "acts on the whole selection". */
  selectionCount?: number;
  /**
   * Bulk-action handlers applied over the CURRENT selection. A selected row whose
   * selection holds >1 task renders these in its ⋯ menu instead of the single-item
   * actions (Edit is dropped), so acting on any selected task acts on all.
   */
  bulkActions?: BulkActionsProps;
}

const TodoListContext = createContext<TodoListContextValue | null>(null);

export function TodoListProvider({
  value,
  children,
}: {
  value: TodoListContextValue;
  children: React.ReactNode;
}) {
  return <TodoListContext.Provider value={value}>{children}</TodoListContext.Provider>;
}

export function useTodoListContext(): TodoListContextValue {
  const ctx = useContext(TodoListContext);
  if (!ctx) {
    throw new Error('useTodoListContext must be used within a TodoListProvider');
  }
  return ctx;
}
